import type { NavigateFunction } from "react-router-dom";

export const INVALID_PATH = "/invalid";

/**
 * Why the visitor landed on the invalid page. Grouped on purpose — the page
 * shows one generic message per group rather than one per failure.
 * - `link`: a public/action link is malformed, expired, or no longer exists
 * - `not-found`: the page or portal address does not exist
 * - `unavailable`: the portal or feature is turned off for this business
 */
export type InvalidReason = "link" | "not-found" | "unavailable";

export type InvalidRouteState = {
  invalidReason: InvalidReason;
};

const INVALID_REASONS: ReadonlySet<string> = new Set<InvalidReason>([
  "link",
  "not-found",
  "unavailable",
]);

/**
 * Router state for a redirect to the invalid page. Carried in history state
 * (not the query string) so the URL stays `/invalid`.
 */
export const invalidRouteState = (
  reason: InvalidReason
): InvalidRouteState => ({ invalidReason: reason });

export const goToInvalid = (navigate: NavigateFunction, reason: InvalidReason) =>
  navigate(INVALID_PATH, {
    replace: true,
    state: invalidRouteState(reason),
  });

/**
 * History state survives a refresh but is missing when `/invalid` is opened
 * directly, so anything unexpected falls back to the not-found message.
 */
export const readInvalidReason = (state: unknown): InvalidReason => {
  const reason =
    state && typeof state === "object"
      ? (state as Partial<InvalidRouteState>).invalidReason
      : undefined;

  return typeof reason === "string" && INVALID_REASONS.has(reason)
    ? (reason as InvalidReason)
    : "not-found";
};

type ReturnClusterInput = {
  /** Slug of this hostname when it is a mapped custom domain. */
  customDomainSlug?: string | null;
  /** `:clusterId` from the route, present on `/:clusterId/*` but not `/invalid`. */
  routeClusterSlug?: string | null;
  /** Cluster of the logged-in account. */
  accountClusterId?: string | number | null;
  /** Cluster whose portal settings were last loaded in this tab. */
  lastClusterId?: string | null;
  decodeClusterSlug: (clusterSlug: string) => string | null;
  encodeClusterId: (clusterId: string) => string;
  isValidClusterId: (clusterId: string | null | undefined) => boolean;
};

/**
 * Picks the portal the "Return to customer portal" button goes back to.
 * `/invalid` carries no cluster in the URL, so it falls back to what the app
 * still knows about the visitor's tenant. Returns null when nothing is known.
 */
export const getReturnClusterSlug = ({
  customDomainSlug,
  routeClusterSlug,
  accountClusterId,
  lastClusterId,
  decodeClusterSlug,
  encodeClusterId,
  isValidClusterId,
}: ReturnClusterInput): string | null => {
  if (customDomainSlug) return customDomainSlug;

  if (
    routeClusterSlug?.startsWith("ci_") &&
    isValidClusterId(decodeClusterSlug(routeClusterSlug))
  ) {
    return routeClusterSlug;
  }

  const fallbackId = [accountClusterId, lastClusterId]
    .map((id) => (id === null || id === undefined ? "" : `${id}`))
    .find((id) => isValidClusterId(id));

  return fallbackId ? encodeClusterId(fallbackId) : null;
};

const LAST_CLUSTER_KEY = "dcdial-last-cluster-id";

/**
 * Remembers the cluster the visitor was last in for this tab. Redux loses it
 * on a reload, so without this a visitor who edits the URL down to `/invalid`
 * (e.g. from `/agreement/agr_…`) would have no portal to return to.
 */
export const rememberClusterId = (clusterId: string) => {
  try {
    sessionStorage.setItem(LAST_CLUSTER_KEY, clusterId);
  } catch {
    // storage blocked — the return button falls back to the toast
  }
};

export const readRememberedClusterId = (): string | null => {
  try {
    return sessionStorage.getItem(LAST_CLUSTER_KEY);
  } catch {
    return null;
  }
};
